import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Minus, Plus, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { isAuthenticated } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      if (response.data.sizes?.length > 0) {
        setSelectedSize(response.data.sizes[0]);
      }
      if (response.data.colors?.length > 0) {
        setSelectedColor(response.data.colors[0]);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedSize || !selectedColor) {
      toast.error('Veuillez sélectionner une taille et une couleur');
      return;
    }
    
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour ajouter au panier');
      navigate('/auth');
      return;
    }

    setAdding(true);
    try {
      await addToCart(product.id, quantity, selectedSize, selectedColor, product);
      toast.success('Ajouté au panier');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout au panier');
    } finally {
      setAdding(false);
    }
  };

  const handleWishlistClick = () => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
      toast.success('Retiré des favoris');
    } else {
      addToWishlist(product);
      toast.success('Ajouté aux favoris');
    }
  };

  const nextImage = () => {
    if (product?.images) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product?.images) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const inWishlist = isInWishlist(product.id);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="product-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Image Gallery */}
          <div className="lg:sticky lg:top-28 lg:h-fit">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative aspect-[3/4] bg-black/10 overflow-hidden"
            >
              <img
                src={product.images[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
              
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-gold hover:text-black transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-gold hover:text-black transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.is_new && (
                  <span className="font-accent text-[10px] uppercase tracking-wider bg-gold text-black px-3 py-1">
                    Nouveau
                  </span>
                )}
                {product.stock < 5 && product.stock > 0 && (
                  <span className="font-accent text-[10px] uppercase tracking-wider bg-kabyle-red text-white px-3 py-1">
                    Stock limité
                  </span>
                )}
                {product.stock === 0 && (
                  <span className="font-accent text-[10px] uppercase tracking-wider bg-black/20 text-foreground px-3 py-1">
                    Rupture de stock
                  </span>
                )}
              </div>
            </motion.div>

            {/* Thumbnails */}
            {product.images.length > 1 && (
              <div className="flex gap-2 mt-4">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-16 h-20 overflow-hidden border-2 transition-colors ${
                      currentImageIndex === idx ? 'border-gold' : 'border-transparent'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="text-xs uppercase tracking-widest text-gold">{product.category}</span>
            <h1 className="font-heading text-3xl md:text-5xl tracking-tight mt-2 mb-4" data-testid="product-title">
              {product.name}
            </h1>
            <p className="price-tag text-gold text-2xl md:text-3xl mb-6" data-testid="product-price">
              {product.price.toFixed(2)} €
            </p>
            
            <p className="text-foreground/70 leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {/* Size Selection */}
            <div className="mb-6">
              <h3 className="text-sm uppercase tracking-widest text-foreground/60 mb-3">Taille</h3>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`px-4 py-2 border transition-colors ${
                      selectedSize === size
                        ? 'border-gold bg-gold text-black'
                        : 'border-black/20 hover:border-gold'
                    }`}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <div className="mb-8">
              <h3 className="text-sm uppercase tracking-widest text-foreground/60 mb-3">Couleur</h3>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`px-4 py-2 border transition-colors flex items-center gap-2 ${
                      selectedColor === color
                        ? 'border-gold text-gold'
                        : 'border-black/20 hover:border-gold text-foreground/70'
                    }`}
                    data-testid={`color-${color.replace(/[^a-zA-Z0-9]/g, '-')}`}
                  >
                    {selectedColor === color && <Check size={14} />}
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <h3 className="text-sm uppercase tracking-widest text-foreground/60 mb-3">Quantité</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 border border-black/20 hover:border-gold transition-colors"
                  data-testid="quantity-minus"
                >
                  <Minus size={16} />
                </button>
                <span className="w-12 text-center font-accent" data-testid="quantity-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="p-2 border border-black/20 hover:border-gold transition-colors"
                  data-testid="quantity-plus"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0 || adding}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-to-cart-btn"
              >
                {adding ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  product.stock === 0 ? 'Rupture de stock' : 'Ajouter au panier'
                )}
              </button>
              <button
                onClick={handleWishlistClick}
                className={`p-4 border transition-colors ${
                  inWishlist 
                    ? 'bg-gold text-black border-gold' 
                    : 'border-black/20 hover:border-gold hover:text-gold'
                }`}
                data-testid="wishlist-toggle-btn"
              >
                <Heart size={20} fill={inWishlist ? 'currentColor' : 'none'} />
              </button>
            </div>

            {/* Stock Info */}
            {product.stock > 0 && product.stock < 10 && (
              <p className="text-kabyle-red text-sm mt-4">
                Plus que {product.stock} en stock
              </p>
            )}

            {/* Features */}
            <div className="mt-12 pt-8 border-t border-black/10">
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div>
                  <p className="text-foreground/40 uppercase tracking-wider mb-1">Livraison</p>
                  <p>Gratuite dès 150€</p>
                </div>
                <div>
                  <p className="text-foreground/40 uppercase tracking-wider mb-1">Retours</p>
                  <p>Sous 14 jours</p>
                </div>
                <div>
                  <p className="text-foreground/40 uppercase tracking-wider mb-1">Paiement</p>
                  <p>Sécurisé</p>
                </div>
                <div>
                  <p className="text-foreground/40 uppercase tracking-wider mb-1">Fabrication</p>
                  <p>Artisanale</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
